/* @flow */
import React, { useContext } from "react";
import Dispatch from "nexia_mobile/flux_like/dispatch";
import Page from "nexia_mobile/components/Page";
import prefixStyles from "nx-components/utils/prefixStyles";
import DetailsHeaderContainer from "nexia_mobile/components/Details/DetailsHeaders/DetailsHeaderContainer";
import ScrollableArea from "nexia_mobile/components/Scrollable/ScrollableArea";
import AppBarButtons from "nexia_mobile/components/TopBar/AppBarButtons";
import AppBarWrapper from "nexia_mobile/components/TopBar/AppBarWrapper";
import CommonAppBarContainer from "nexia_mobile/components/TopBar/CommonAppBarContainer";
import {
  withColorsProvider,
  type ColorSet,
} from "nx-components/ColorsProvider";
import { showConfirmationDialog } from "nexia_mobile/components/Dialogs/ConfirmationDialog";
import { showAlertDialog } from "nexia_mobile/components/Dialogs/AlertDialog";
import BackboneListener from "nexia_mobile/components/BackboneListener";
import Location from "nexia_mobile/models/location";
import Device from "nexia_mobile/models/device";
import Schema from "nexia_mobile/models/schema";
import Section from "nx-components/Section";
import ItemList from "nx-components/ItemList";
import Theme from "nx-components/Theme";
import Store from "nexia_mobile/flux_like/store";
import UserLanguageContext from "nx-components/ContextWrappers/UserLanguageContext";

const ITEM_PADDING = Theme.Padding.CONTENT;

type Props = {
  model: Location,
  openBuildingEdit: (model: Schema) => ?Promise<void>,
  onDelete: () => ?Promise<void>,
  colors: ColorSet,
};

const BuildingDetails = (props: Props) => {
  const languageContext = useContext(UserLanguageContext);
  const language =
    languageContext.localeSet.nexia_mobile.details.BuildingDetails;
  const styles = prefixStyles({
    section: {
      borderTop: "1px solid #E3E3E3",
    },
    detail: {
      fontSize: ".9em",
      flexDirection: "row",
    },
    itemDeviceContainer: {
      display: "flex",
      alignItems: "top",
      whiteSpace: "normal",
      color: props.colors.TEXT,
      outline: "none",
      transition: "color 0.1s",
      cursor: "default",
      backgroundColor: "transparent",
      fontSize: "0.85em",
    },
    itemDeviceLabel: {
      paddingTop: ITEM_PADDING,
      paddingRight: ITEM_PADDING,
      paddingBottom: ITEM_PADDING,
      paddingLeft: 0,
      minWidth: 0, // fixes a bizarre bug with text-overflow
      flex: "1 1 auto",
    },
    itemDeviceChildren: {
      paddingTop: ITEM_PADDING,
      whiteSpace: "normal",
      height: "100%",
      flex: "0 0 auto",
      minWidth: 0, // fixes a bizarre bug with text-overflow
    },
  });

  const handleDelete = () => {
    const locations = Store.session.get("locations");
    if (locations.length === 1) {
      const message = language.minOneBuilding;
      showAlertDialog({ message });
      return;
    }

    const { model, onDelete } = props;
    const deviceCollection = model.get("devices").models;
    if (deviceCollection.length === 0) {
      showConfirmationDialog({
        message: language.deleteConfirmation,
        cancelOnBackgroundClick: false,
        cancelOnRequestClose: false,
        confirmButtonText: language.delete,
        onConfirm: () => {
          Dispatch("RUN_PROMISE_WITH_SPINNER", () =>
            model.destroy({ wait: true }).then(onDelete)
          );
        },
      });
    } else {
      const message = language.hasDevicesCannotDelete;
      showAlertDialog({ message });
    }
  };

  const handleEdit = () => {
    const { model, openBuildingEdit } = props;
    Dispatch("RUN_PROMISE_WITH_SPINNER", () =>
      model.fetch().then(() => {
        model.createEditSchema().then(openBuildingEdit);
      })
    );
  };

  const buildingDevicesList = (devices) => {
    return devices.map((device) => {
      const key = device.get("id");
      const name = device.get("name");
      return (
        <div key={key} style={styles.detail}>
          {name}
        </div>
      );
    });
  };

  const { model } = props;

  return (
    <BackboneListener listenTo={model} events={["change", "sync"]}>
      {() => {
        const name: string = model.get("name");
        const postalCode: string = model.get("postal_code");
        const deviceCollection: Array<Device> = model.get("devices").models;

        const detailsItems = [
          {
            id: "name",
            label: language.nameLabel,
            children: <span style={styles.detail}>{name}</span>,
          },
          {
            id: "postalCode",
            label: language.postalCodeLabel,
            children: <span style={styles.detail}>{postalCode}</span>,
          },
        ];

        return (
          <Page name="BuildingDetails" fullyLoaded>
            <AppBarWrapper>
              <CommonAppBarContainer
                title={model.get("name")}
                iconRight={
                  <AppBarButtons onDelete={handleDelete} onEdit={handleEdit} />
                }
              />
            </AppBarWrapper>
            <ScrollableArea>
              <DetailsHeaderContainer model={model} />
              <Section
                style={styles.section}
                label={language.buildingDetailsLabel}
              >
                <ItemList
                  id="detail-items"
                  items={detailsItems}
                  style={{ fontSize: "0.85em" }}
                />
                <div style={styles.itemDeviceContainer}>
                  <div style={styles.itemDeviceLabel}>{language.devices}</div>
                  <div style={styles.itemDeviceChildren}>
                    {deviceCollection.length !== 0 ? (
                      buildingDevicesList(deviceCollection)
                    ) : (
                      <span style={styles.detail}>{language.none}</span>
                    )}
                  </div>
                </div>
              </Section>
            </ScrollableArea>
          </Page>
        );
      }}
    </BackboneListener>
  );
};

export default withColorsProvider(BuildingDetails);



// WEBPACK FOOTER //
// ./src/components/Details/BuildingDetails.jsx